import { useEffect, useRef } from 'react'
import { dataHora } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { MensagemDaConsulta } from '../../types/documentos'
import { CartaoDaResposta } from './CartaoDaResposta'

/** A pergunta, alinhada à direita como numa conversa — e em azul institucional, que é a voz de quem usa. */
function Pergunta({ texto, quando }: { texto: string; quando?: string }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-niteroi-700 px-4 py-2.5 text-[0.95rem] leading-relaxed text-white">
        {texto}
      </p>
      {quando && <span className="pr-1 text-xs text-slate-400">{dataHora(quando)}</span>}
    </div>
  )
}

/**
 * As perguntas e respostas de uma conversa, e a pergunta em andamento no fim.
 *
 * A espera pode passar de meio minuto, e a tela diz isso com todas as letras em vez de um giro sem
 * fim — com `aria-live`, para quem usa leitor de tela saber que algo está acontecendo e quando
 * termina. Ao chegar mensagem nova, a tela desce até ela.
 *
 * @param props as mensagens, a pergunta em andamento e o que fazer para repetir uma pergunta
 * @returns o fio
 */
export function FioDaConversa({
  mensagens,
  pendente,
  aoRepetir,
}: {
  mensagens: MensagemDaConsulta[]
  pendente: string | null
  aoRepetir: (pergunta: string) => void
}) {
  const fim = useRef<HTMLDivElement>(null)
  const quantas = mensagens.length + (pendente ? 1 : 0)

  useEffect(() => {
    fim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [quantas])

  return (
    <div className="space-y-6" aria-live="polite">
      {mensagens.map((mensagem, indice) => (
        <div key={mensagem.id} className="space-y-3">
          <Pergunta texto={mensagem.pergunta} quando={mensagem.geradoEm} />
          {mensagem.situacao === 'EM_ANDAMENTO' ? (
            <Pensando />
          ) : (
            <CartaoDaResposta
              mensagem={mensagem}
              // Só a última pode ser repetida: repetir uma do meio embaralharia a ordem da conversa.
              aoRepetir={indice === mensagens.length - 1 ? () => aoRepetir(mensagem.pergunta) : undefined}
            />
          )}
        </div>
      ))}
      {pendente && (
        <div className="space-y-3">
          <Pergunta texto={pendente} />
          <Pensando />
        </div>
      )}
      <div ref={fim} />
    </div>
  )
}

function Pensando() {
  return (
    <div role="status" className="flex items-center gap-3 rounded-xl bg-white px-5 py-4 text-sm text-slate-600 ring-1 ring-slate-200/70">
      <span className="flex gap-1" aria-hidden="true">
        <span className="h-2 w-2 animate-pulse rounded-full bg-niteroi-400" />
        <span className="h-2 w-2 animate-pulse rounded-full bg-niteroi-400 [animation-delay:200ms]" />
        <span className="h-2 w-2 animate-pulse rounded-full bg-niteroi-400 [animation-delay:400ms]" />
      </span>
      {TEXTOS.consulta.pensando}
    </div>
  )
}
